import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Download, Trash, Copy, PaperPlaneTilt, Pencil, FloppyDisk } from '@phosphor-icons/react';
import { computeBundleTotals, computeOrderTotals, groupSelections, computePro75MarginExample } from '../lib/bundleHelpers';
import { Pro75MarginExample } from './Pro75MarginExample';
import { useAuth } from '../auth/AuthContext';
import ConfirmDialog from './ConfirmDialog';
import TermsAcceptanceLabel from './TermsAcceptanceLabel';
import PromptDialog from './PromptDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SavedBundleRow = ({ bundle, isExpanded, onToggle, onRemove, onDuplicate, onEdit, isCustomer }) => {
  const totals = computeBundleTotals(bundle);
  const grouped = groupSelections(bundle.products);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  return (
    <div className="bg-black/40 border border-border-color rounded-lg overflow-hidden" data-testid={`saved-bundle-${bundle.number}`}>
      <div className="p-3 flex items-center justify-between cursor-pointer hover:bg-surface-hover" onClick={() => onToggle(bundle.id)}>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-xs font-mono text-gold">Saver {String(bundle.number).padStart(2, '0')}</span>
            <span className="text-sm font-semibold text-text-primary truncate">{bundle.name}</span>
            {bundle.source === 'planner' && (
              <span
                className="inline-flex items-center px-1.5 py-0.5 text-[9px] uppercase tracking-wider bg-gold/15 text-gold border border-gold/30 rounded"
                title="Created by Smart Saver Planner"
                data-testid={`saved-bundle-auto-badge-${bundle.number}`}
              >
                Auto
              </span>
            )}
          </div>
          <p className="text-xs text-text-secondary mt-1" data-testid={`saved-bundle-totals-${bundle.number}`}>
            {bundle.products.length} products • Subtotal <span className="text-text-primary">${totals.subtotal.toFixed(2)}</span>
            <span className="mx-1">·</span>
            Discount <span className="text-success">${totals.customerSaving.toFixed(2)}{!isCustomer && ` (${totals.savingPercent.toFixed(0)}%)`}</span>
            <span className="mx-1">·</span>
            Saver Price <span className="text-gold font-semibold">${totals.bundlePrice.toFixed(2)}</span>
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {onEdit && (
            <button
              onClick={(e) => { e.stopPropagation(); onEdit(bundle.id); }}
              data-testid={`edit-bundle-${bundle.number}`}
              className="text-xs text-text-secondary hover:text-gold px-2"
              aria-label="Edit saver"
              title="Edit saver (loads it back into the builder)"
            >
              <Pencil size={14} weight="bold" />
            </button>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); onDuplicate(bundle.id); }}
            data-testid={`duplicate-bundle-${bundle.number}`}
            className="text-xs text-text-secondary hover:text-gold px-2"
            aria-label="Duplicate saver"
            title="Duplicate this saver"
          >
            <Copy size={14} weight="bold" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); setConfirmOpen(true); }}
            data-testid={`remove-bundle-${bundle.number}`}
            className="text-xs text-text-secondary hover:text-danger px-2"
            aria-label="Remove saver"
          >
            <Trash size={14} weight="bold" />
          </button>
          <span className="text-text-secondary text-xs">{isExpanded ? '▾' : '▸'}</span>
        </div>
      </div>
      {isExpanded && (
        <div className="px-3 pb-3 pt-1 border-t border-border-color text-xs space-y-1">
          {bundle.comments && (
            <div className="bg-black/40 rounded p-2 mb-2 text-text-secondary italic whitespace-pre-wrap" data-testid={`bundle-comments-${bundle.number}`}>
              {bundle.comments}
            </div>
          )}
          {grouped.map((g) => {
            const unit = Math.round(((typeof g.unitPrice === 'number' && Number.isFinite(g.unitPrice))
              ? g.unitPrice : Number(g.wholesalePrice || 0)) * 100) / 100;
            return (
              <div key={`${g.id}-${g.selectedFlavour || 'def'}`} className="flex justify-between text-text-primary">
                <span className="truncate pr-2">
                  {g.name}
                  {g.selectedFlavour && <span className="text-gold"> – {g.selectedFlavour}</span>}
                  {g.qty > 1 && <span className="ml-1 text-gold font-semibold">× {g.qty}</span>}
                </span>
                <span className="text-text-secondary shrink-0">${(unit * g.qty).toFixed(2)}</span>
              </div>
            );
          })}
          <div className="border-t border-border-color pt-2 mt-2 text-text-secondary space-y-0.5">
            <div className="flex justify-between"><span>Customer saving:</span><span className="text-success">${totals.customerSaving.toFixed(2)}{!isCustomer && ` (${totals.savingPercent.toFixed(0)}%)`}</span></div>
          </div>
        </div>
      )}
      <ConfirmDialog
        open={confirmOpen}
        title="Delete saver?"
        message={`Delete "Saver ${String(bundle.number).padStart(2, '0')} — ${bundle.name}"? This cannot be undone.`}
        confirmLabel="Delete"
        busy={busy}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={async () => {
          setBusy(true);
          try { await onRemove(bundle.id); } finally {
            setBusy(false);
            setConfirmOpen(false);
          }
        }}
        testIdPrefix={`confirm-remove-bundle-${bundle.number}`}
      />
    </div>
  );
};

const OrderAggregate = ({ bundles, isCustomer, banked = 0 }) => {
  const o = computeOrderTotals(bundles);
  const appliedBanked = Math.max(0, Math.min(Number(banked || 0), o.discountedTotal));
  const finalTotal = Math.max(0, o.discountedTotal - appliedBanked);
  const allProducts = bundles.flatMap((b) => b.products || []);
  const marginExample = computePro75MarginExample(allProducts, o.totalDiscount);
  return (
    <div
      className="mb-4 p-3 rounded-lg border border-gold/40 bg-gold/5 text-sm space-y-1"
      data-testid="order-aggregate"
    >
      <p className="text-xs uppercase tracking-wider text-gold font-semibold mb-2">Order Totals ({bundles.length} saver{bundles.length === 1 ? '' : 's'})</p>
      <div className="flex justify-between"><span className="text-text-secondary">Subtotal:</span><span className="text-text-primary" data-testid="order-subtotal">${o.subtotal.toFixed(2)}</span></div>
      <div className="flex justify-between"><span className="text-text-secondary">Total Discount:</span><span className="text-success" data-testid="order-total-discount">${o.totalDiscount.toFixed(2)}{!isCustomer && ` (${o.totalDiscountPct.toFixed(0)}%)`}</span></div>
      <div className="flex justify-between text-base pt-1 border-t border-gold/20 mt-1"><span className="text-text-primary font-semibold">{appliedBanked > 0 ? 'Pre-credit total' : 'Order Total'}:</span><span className="text-gold font-bold" data-testid="order-discounted-total">${o.discountedTotal.toFixed(2)}</span></div>
      {appliedBanked > 0 && (
        <>
          <div
            className="flex justify-between"
            data-testid="order-banked-credit-applied"
          >
            <span className="text-gold">
              Prepay credit applied
              <span className="text-[10px] text-text-secondary ml-1">
                (banked ${Number(banked).toFixed(2)})
              </span>
            </span>
            <span className="text-gold font-semibold">−${appliedBanked.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-base pt-1 border-t border-gold/20 mt-1">
            <span className="text-text-primary font-semibold">Pre-GST total:</span>
            <span
              className="text-gold font-bold"
              data-testid="order-final-total"
            >${finalTotal.toFixed(2)}</span>
          </div>
        </>
      )}
      <Pro75MarginExample example={marginExample} context="order" />
    </div>
  );
};

export const SavedBundlesList = ({
  savedBundles, expandedBundleIds,
  onToggleExpansion, onExpandAll, onCollapseAll, onRemove, onDuplicate, onEdit, onClearAll, onExportAll, onSubmitOrder, onSaveOrder,
}) => {
  const { user } = useAuth();
  const isCustomer = user?.role === 'customer';
  const [submitting, setSubmitting] = useState(false);
  const [submitMsg, setSubmitMsg] = useState(null);
  const [submitConfirmOpen, setSubmitConfirmOpen] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);
  const [saveOrderMsg, setSaveOrderMsg] = useState(null);
  const [clearing, setClearing] = useState(false);
  const [clearMsg, setClearMsg] = useState(null);
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false);
  const [saveOrderPromptOpen, setSaveOrderPromptOpen] = useState(false);
  const [prepayConfig, setPrepayConfig] = useState({ enabled: true, label: '', banked: 0 });

  // Fetch the prepay checkbox config for the bundle owner — now a per-customer
  // setting, sourced via /users/{id}/effective-config.
  const ownerCustomerId = savedBundles[0]?.customerId;
  useEffect(() => {
    if (!ownerCustomerId) return;
    let cancelled = false;
    axios.get(`${API}/users/${ownerCustomerId}/effective-config`).then((res) => {
      if (cancelled) return;
      setPrepayConfig({
        enabled: res.data?.prepayEnabled !== false,
        label: res.data?.prepayLabel || '',
        banked: Number(res.data?.prepayBankedDiscount || 0),
      });
    }).catch(() => { /* silently use defaults */ });
    return () => { cancelled = true; };
  }, [ownerCustomerId]);

  if (savedBundles.length === 0) return null;
  const allExpanded = expandedBundleIds.size === savedBundles.length;

  const doSubmit = async ({ secondaryAccepted, reference } = {}) => {
    if (!onSubmitOrder) return;
    setSubmitConfirmOpen(false);
    setSubmitting(true);
    setSubmitMsg(null);
    try {
      const res = await onSubmitOrder({
        prepayRequested: !!secondaryAccepted,
        customerReference: reference || '',
      });
      const prepayNote = secondaryAccepted ? ' Prepayment terms flagged on the order.' : '';
      setSubmitMsg({ kind: 'ok', text: `Order sent to ${res?.sent_to?.length ?? '?'} recipient${res?.sent_to?.length === 1 ? '' : 's'}.${prepayNote}` });
    } catch (e) {
      setSubmitMsg({ kind: 'err', text: e?.response?.data?.detail || e.message || 'Submit failed' });
    } finally {
      setSubmitting(false);
      setTimeout(() => setSubmitMsg(null), 5000);
    }
  };

  const doSaveOrder = async (rawName) => {
    if (!onSaveOrder) return;
    const trimmed = (rawName || '').trim();
    if (!trimmed) {
      setSaveOrderMsg({ kind: 'err', text: 'Please provide a name.' });
      setTimeout(() => setSaveOrderMsg(null), 4000);
      return;
    }
    const today = new Date().toISOString().slice(0, 10);
    const fullName = `${today} ${trimmed}`;
    setSavingOrder(true);
    setSaveOrderMsg(null);
    try {
      const res = await onSaveOrder(fullName);
      setSaveOrderMsg({ kind: 'ok', text: `Saved as "${res?.name || fullName}". Find it under Saved Orders.` });
      setSaveOrderPromptOpen(false);
    } catch (e) {
      setSaveOrderMsg({ kind: 'err', text: e?.response?.data?.detail || e.message || 'Save failed' });
    } finally {
      setSavingOrder(false);
      setTimeout(() => setSaveOrderMsg(null), 5000);
    }
  };

  const doClearAll = async () => {
    if (!onClearAll) return;
    setClearConfirmOpen(false);
    setClearing(true);
    setClearMsg(null);
    try {
      await onClearAll();
      setClearMsg({ kind: 'ok', text: `Cleared ${savedBundles.length} bundle${savedBundles.length === 1 ? '' : 's'}.` });
    } catch (e) {
      setClearMsg({ kind: 'err', text: e?.response?.data?.detail || e.message || 'Clear failed' });
    } finally {
      setClearing(false);
      setTimeout(() => setClearMsg(null), 5000);
    }
  };

  return (
    <div className="mt-6 bg-surface border border-border-color rounded-xl p-6" data-testid="saved-bundles-panel">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold font-heading text-gold">Saved Savers ({savedBundles.length})</h2>
        <div className="flex items-center gap-3">
          <button
            onClick={allExpanded ? onCollapseAll : onExpandAll}
            data-testid="toggle-all-bundles"
            className="text-xs text-text-secondary hover:text-gold"
          >
            {allExpanded ? 'Collapse all' : 'Expand all'}
          </button>
          <button
            onClick={() => setClearConfirmOpen(true)}
            disabled={clearing}
            data-testid="clear-bundles-button"
            className="text-xs text-text-secondary hover:text-danger disabled:opacity-50"
          >
            {clearing ? 'Clearing…' : 'Clear all'}
          </button>
        </div>
      </div>
      {clearMsg && (
        <div
          data-testid="clear-bundles-status"
          className={`mb-3 text-xs px-3 py-2 rounded ${clearMsg.kind === 'ok' ? 'text-success bg-success/10 border border-success/30' : 'text-danger bg-danger/10 border border-danger/30'}`}
        >
          {clearMsg.text}
        </div>
      )}
      <div className="space-y-3 mb-4">
        {savedBundles.map((bundle) => (
          <SavedBundleRow
            key={bundle.id}
            bundle={bundle}
            isExpanded={expandedBundleIds.has(bundle.id)}
            onToggle={onToggleExpansion}
            onRemove={onRemove}
            onDuplicate={onDuplicate}
            onEdit={onEdit}
            isCustomer={isCustomer}
          />
        ))}
      </div>

      <OrderAggregate bundles={savedBundles} isCustomer={isCustomer} banked={prepayConfig.banked} />

      <button
        onClick={onExportAll}
        data-testid="export-all-button"
        className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gold text-black font-bold rounded-lg hover:bg-gold-hover"
      >
        <Download size={20} weight="bold" />
        Export All as CSV
      </button>
      {onSubmitOrder && (
        <button
          onClick={() => setSubmitConfirmOpen(true)}
          disabled={submitting}
          data-testid="submit-order-button"
          className="mt-3 w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-gold text-gold font-bold rounded-lg hover:bg-gold hover:text-black disabled:opacity-50"
        >
          <PaperPlaneTilt size={20} weight="bold" />
          {submitting ? 'Submitting…' : 'Submit Order'}
        </button>
      )}
      {onSaveOrder && (
        <button
          onClick={() => setSaveOrderPromptOpen(true)}
          disabled={savingOrder}
          data-testid="save-order-button"
          className="mt-3 w-full flex items-center justify-center gap-2 px-4 py-3 border border-border-color text-text-primary font-semibold rounded-lg hover:bg-surface-hover disabled:opacity-50"
          title="Snapshot this order to your Saved Orders so you can load it later. Current savers stay editable."
        >
          <FloppyDisk size={20} weight="bold" />
          {savingOrder ? 'Saving…' : 'Save Order'}
        </button>
      )}
      {submitMsg && (
        <div
          data-testid="submit-order-status"
          className={`mt-2 text-xs px-3 py-2 rounded ${submitMsg.kind === 'ok' ? 'text-success bg-success/10 border border-success/30' : 'text-danger bg-danger/10 border border-danger/30'}`}
        >
          {submitMsg.text}
        </div>
      )}
      {saveOrderMsg && (
        <div
          data-testid="save-order-status"
          className={`mt-2 text-xs px-3 py-2 rounded ${saveOrderMsg.kind === 'ok' ? 'text-success bg-success/10 border border-success/30' : 'text-danger bg-danger/10 border border-danger/30'}`}
        >
          {saveOrderMsg.text}
        </div>
      )}
      <ConfirmDialog
        open={clearConfirmOpen}
        title="Clear all saved savers?"
        message={`This will permanently remove all ${savedBundles.length} saved saver${savedBundles.length === 1 ? '' : 's'} for this customer. This cannot be undone.`}
        confirmLabel={`Clear all ${savedBundles.length}`}
        busy={clearing}
        onCancel={() => setClearConfirmOpen(false)}
        onConfirm={doClearAll}
        testIdPrefix="confirm-clear-bundles"
      />
      <PromptDialog
        open={saveOrderPromptOpen}
        title="Name this order"
        message={`A YYYY-MM-DD date prefix will be added automatically.\nExample: today → "${new Date().toISOString().slice(0,10)} <your name>"`}
        placeholder="e.g. Pre-comp Stack"
        confirmLabel="Save Order"
        busy={savingOrder}
        onCancel={() => setSaveOrderPromptOpen(false)}
        onConfirm={doSaveOrder}
        testIdPrefix="prompt-save-order"
      />
      <ConfirmDialog
        open={submitConfirmOpen}
        title="Submit order?"
        message={`Submit ${savedBundles.length} saver${savedBundles.length === 1 ? '' : 's'} as an order. An email will be sent to the configured recipients, and your saved savers will be cleared on success.`}
        confirmLabel={`Submit ${savedBundles.length} saver${savedBundles.length === 1 ? '' : 's'}`}
        destructive={false}
        busy={submitting}
        onCancel={() => setSubmitConfirmOpen(false)}
        onConfirm={doSubmit}
        testIdPrefix="confirm-submit-order"
        requireAcceptance
        acceptanceLabel={<TermsAcceptanceLabel />}
        secondaryAcceptanceEnabled={prepayConfig.enabled && !!prepayConfig.label}
        secondaryAcceptanceLabel={prepayConfig.label}
        referenceInputEnabled
        referenceInputLabel="Your reference (optional)"
        referenceInputPlaceholder="e.g. PO-12345 — appears on the Unleashed inventory file"
      />
    </div>
  );
};

export default SavedBundlesList;
